import FileUploadNew from '@components/FileUploadNew';
import { Flex, Text } from '@retracedgmbh/ui-components-lib';
import type { UploadFileData } from '@/hooks/useFileUploadNew';
import useTranslator from '@/hooks/useTranslator';
import type {
  ClassifyResultItem,
  FileClassificationType,
} from '@/services/api/types/ai-processing';
import UploadedFileCard from './UploadedFileCard';

const MAX_FILES = 5;
const MAX_FILE_SIZE = 20 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx'];

interface BulkFileUploadProps {
  batchKey: number;
  uploadedFiles: UploadFileData[];
  onUploadFinish: (files: UploadFileData[]) => void;
  isDisabled?: boolean;
  classifyResults?: ClassifyResultItem[];
  typeOverrides: Record<string, FileClassificationType>;
  onTypeChange: (fileId: string, type: FileClassificationType) => void;
}

export default function BulkFileUpload({
  batchKey,
  uploadedFiles,
  onUploadFinish,
  isDisabled,
  classifyResults,
  typeOverrides,
  onTypeChange,
}: BulkFileUploadProps) {
  const { getTranslation } = useTranslator({ page: 'ai-processing' });
  const resultsByFileId = new Map(classifyResults?.map(r => [r.fileId, r]));

  return (
    <Flex direction='column' gap='2xl'>
      {/* Info banner */}
      <Flex
        padding='xl'
        gap='sm'
        alignItems='start'
        className='tw:rounded-md tw:border tw:border-orange-500 tw:bg-orange-100'
      >
        <i className='fa-solid fa-circle-exclamation tw:text-yellow-700 tw:mt-0.5' />
        <Text size='small' className='tw:text-amber-800'>
          {getTranslation('upload.infoBanner')}
        </Text>
      </Flex>

      {/* Upload section */}
      <Flex direction='column' gap='sm'>
        <Text size='base' weight='semibold'>
          {getTranslation('upload.title')}
        </Text>
        <FileUploadNew
          key={batchKey}
          isMultiple
          maxFiles={MAX_FILES}
          acceptedFileTypes={ACCEPTED_FILE_TYPES}
          acceptedMaxFileSize={MAX_FILE_SIZE}
          onUploadFinish={onUploadFinish}
          isCompact={uploadedFiles.length > 0}
          isPreviewHidden
          disabled={isDisabled}
        />
      </Flex>

      {/* Uploaded file cards */}
      {uploadedFiles.length > 0 && (
        <Flex direction='column' gap='md'>
          <Text size='base' weight='semibold'>
            {getTranslation('upload.uploadedFiles', {
              count: uploadedFiles.length,
            })}
          </Text>
          <Flex direction='column' gap='sm'>
            {uploadedFiles.map(file => {
              const fileId = file.id as string;
              const result = resultsByFileId.get(fileId);
              const classificationType =
                typeOverrides[fileId] ??
                (result?.classification?.type as
                  | FileClassificationType
                  | undefined) ??
                null;

              return (
                <UploadedFileCard
                  key={file.uploadFileId}
                  fileName={(file.name as string) ?? 'Unknown file'}
                  fileSize={file.size as number | undefined}
                  status={result?.status ?? 'processing'}
                  classificationType={classificationType}
                  onTypeChange={type => onTypeChange(fileId, type)}
                />
              );
            })}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
}
